import { yupResolver } from '@hookform/resolvers/yup';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Dialog, DialogContent } from '~/shared/components/Dialog';
import { Form } from '~/shared/components/form';
import { Link } from '~/shared/components/Link';
import Spinner from '~/shared/components/Spinner/Spinner';
import { useFrame } from '~/shared/utils';
import { useTranslation } from '~/shared/utils/translation';
import { theme } from '~/theme';
import { useNotifyWhenBack } from './api';
import { useValidationSchema } from './lib/useValidationSchema';
import {
    DialogContainer,
    Label,
    Paragraph,
    StyledButton,
    StyledCheckmark,
    StyledContentWrapper,
    StyledEmailPolicy,
    StyledFormWrapper,
    StyledInput,
    StyledSubmitSuccess,
} from './styled';

interface Props {
    isOpen: boolean;
    productId: string;
    onOpenChange(isOpen: boolean): void;
}

interface NotifyWhenBackFields {
    email: string;
}

export const NotifyWhenBack: React.FC<Props> = ({ isOpen, onOpenChange, productId }) => {
    const { translate } = useTranslation();
    const { data: frame } = useFrame();
    const [isSubmitSuccessFul, setIsSubmitSuccessFul] = useState(false);
    const [isSubmitError, setIsSubmitError] = useState(false);
    const validationSchema = useValidationSchema();

    const methods = useForm<NotifyWhenBackFields>({
        mode: 'onChange',
        reValidateMode: 'onChange',
        resolver: yupResolver(validationSchema),
    });

    const {
        formState: { isValid },
    } = methods;

    const emailValue = methods.watch('email');
    const { reset } = methods;

    const notifyMeData = {
        email: emailValue,
        productId: productId,
        source: 'web',
    };

    const { refetch, isFetching } = useNotifyWhenBack(notifyMeData);

    const handleSubmit = async () => {
        const { data } = await refetch();
        if (data?.isFetchSuccessful) {
            setIsSubmitSuccessFul(true);
        } else {
            setIsSubmitError(true);
        }
    };

    const handleClose = () => {
        setIsSubmitSuccessFul(false);
        setIsSubmitError(false);
    };

    const onDialogOpenChange = (value: boolean) => {
        reset();

        onOpenChange?.(value);
    };

    return (
        <Dialog open={isOpen} onOpenChange={onDialogOpenChange}>
            <DialogContent>
                <DialogContainer>
                    {isSubmitError ? (
                        <StyledContentWrapper>
                            <h2>{translate('pdp.notifyBannerErrorTitle')}</h2>
                            <div>{translate('pdp.notifyBannerErrorInfo')}</div>

                            <StyledButton onClick={handleClose}>
                                {translate('pdp.notifyBannerContinue')}
                            </StyledButton>
                        </StyledContentWrapper>
                    ) : isSubmitSuccessFul ? (
                        <StyledSubmitSuccess>
                            <StyledContentWrapper>
                                <StyledCheckmark />
                                <h2> {translate('pdp.notifyBannerSuccessTitle')}</h2>
                                <div>{translate('pdp.notifyBannerSuccessInfo')}</div>
                            </StyledContentWrapper>

                            <StyledButton onClick={handleClose}>
                                {translate('pdp.notifyBannerContinue')}
                            </StyledButton>
                        </StyledSubmitSuccess>
                    ) : (
                        <>
                            <h2> {translate('pdp.notifyBannerTitle')}</h2>

                            <Paragraph>{translate('pdp.notifyBannerDescription')}</Paragraph>
                            <StyledFormWrapper>
                                <Form methods={methods} onSubmit={handleSubmit}>
                                    <Label style={{ display: 'flex' }}>Email</Label>
                                    <StyledInput
                                        appendWhenValid={false}
                                        name={'email'}
                                        maxLength={50}
                                        required
                                    />
                                    {isFetching ? (
                                        <Spinner />
                                    ) : (
                                        <StyledButton type="submit" disabled={!isValid}>
                                            {translate('pdp.notifyBannerSubmit')}
                                        </StyledButton>
                                    )}
                                </Form>
                            </StyledFormWrapper>

                            <StyledEmailPolicy>
                                <span>{translate('pdp.notifyEmailPolicy')}</span>
                                <Link
                                    color={theme.colors.black}
                                    noHover
                                    target="_blank"
                                    title={translate('pdp.notifyEmailPolicyLink')}
                                    href={frame.market?.personalDataPolicyLink}
                                >
                                    <span style={{ textDecoration: 'underline' }}>
                                        {translate('pdp.notifyEmailPolicyLink')}
                                    </span>
                                </Link>
                            </StyledEmailPolicy>
                        </>
                    )}
                </DialogContainer>
            </DialogContent>
        </Dialog>
    );
};
